import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { format, isSameMonth, isSameYear, parse } from 'date-fns';
import {
  api,
  CHRONOLOGY_UNDO_REDO_HISTORY_TAG,
  CHRONOLOGY_TAG,
  isEndpointLockError,
  TIMELINE_UNDO_REDO_HISTORY_TAG,
  TIMELINE_TAG,
  useLazyCreateChronologyEntryQuery,
  useLazyCreateTimelineEntryQuery,
  useLazyGetCaseProvidersQuery,
  useLazyUpdateChronologyEntryCommentQuery,
  useLazyUpdateChronologyEntryDateQuery,
  useLazyUpdateChronologyEntryPagesQuery,
  useLazyUpdateChronologyEntryProviderQuery,
  useLazyUpdateChronologyEntrySummaryQuery,
  useLazyUpdateTimelineEntryCommentQuery,
  useLazyUpdateTimelineEntryDateQuery,
  useLazyUpdateTimelineEntryPagesQuery,
  useLazyUpdateTimelineEntryProviderQuery,
  useLazyUpdateTimelineEntrySummaryQuery,
  useLazyUpdateTimelineEntryTimeQuery,
  useLazyUpdateChronologyEntryTimeQuery,
} from '@/api';
import {
  ActiveBundlePageType,
  selectActiveBundlePageType,
  selectCaseIdData,
  selectCaseProvidersList,
  selectSharedDataActiveTimelineChronologyType,
  selectTimelineChronologyActiveEntryIndex,
  selectTimelineChronologyData,
  selectTimelineChronologySelectedEntryIds,
  setTimelineChronologyActiveEntryIndex,
  setTimelineChronologySelectedEntryIds,
  TimelineChronologyEntry,
  TimelineChronologyType,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { InsertableListVirtuosoRef, ReactVirtualizedTimelineRef, Toast } from '@repo/atomic-components';
import { BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, useFletchersBroadcast } from '@/broadcast';
import {
  areCommentsDifferent,
  areDatesDifferent,
  arePagesDifferent,
  areProvidersDifferent,
  areSummariesDifferent,
  areTimesDifferent,
  getIndexThatIncludesInsertZones,
  parseNumbers,
  // sortTimelineChronologyByDateAscending,
  transformPagesToPageNumbers,
  validatePagesInput,
} from '../utils';

export interface EntryData {
  date: Date | null;
  time: Date | null;
  provider: ProviderOption | null;
  summary: string;
  comment: string;
  /** Pages input */
  pages: string;
}

export type EntryDataKey = keyof EntryData;
export type EntryDataValue = EntryData[keyof EntryData];
export type ProviderOption = { name: string; code: string };
type RequestListAccumulator = Promise<{ success: boolean }>[];

const initialEntryData: EntryData = {
  date: null,
  time: null,
  provider: null,
  summary: '',
  comment: '',
  pages: '',
};

export const useTimelineChronologyData = () => {
  const newEntryId = useId();

  const timelineRef = useRef<ReactVirtualizedTimelineRef>(null);
  const listRef = useRef<InsertableListVirtuosoRef>(null);
  const toastRef = useRef<Toast>(null);

  const dispatch = useAppDispatch();
  const { sendMessageWithoutResponse } = useFletchersBroadcast();

  const caseId = useAppSelector(selectCaseIdData);
  const activeBundlePageType = useAppSelector(selectActiveBundlePageType);
  const activeTimelineChronologyType = useAppSelector(selectSharedDataActiveTimelineChronologyType);
  const data = useAppSelector(selectTimelineChronologyData);
  const activeEntryIndex = useAppSelector(selectTimelineChronologyActiveEntryIndex);
  const selectedEntryIds = useAppSelector(selectTimelineChronologySelectedEntryIds);
  const caseProvidersList = useAppSelector(selectCaseProvidersList);

  const [newEntryIndex, setNewEntryIndex] = useState<number | null>(null);
  const [editEntryIndex, setEditEntryIndex] = useState<number | null>(null);
  const [editEntryId, setEditEntryId] = useState<string | null>(null);
  const [listEntries, setListEntries] = useState<(string | TimelineChronologyEntry | null | undefined)[]>([]);
  const [currentListEntries, setCurrentListEntries] = useState<TimelineChronologyEntry[]>([]);
  const [entryData, setEntryData] = useState<EntryData>(initialEntryData);
  const [isPagesInputError, setIsPagesInputError] = useState<boolean>(false);

  const [triggerGetCaseProviders] = useLazyGetCaseProvidersQuery();
  const [
    triggerCreateTimelineEntry,
    {
      data: createTimelineEntryData,
      isSuccess: isCreateTimelineEntrySuccess,
      isError: isCreateTimelineEntryError,
      reset: createTimelineEntryReset,
    },
  ] = useLazyCreateTimelineEntryQuery();
  const [
    triggerCreateChronologyEntry,
    {
      data: createChronologyEntryData,
      isSuccess: isCreateChronologyEntrySuccess,
      isError: isCreateChronologyEntryError,
      error: createChronologyEntryError,
      reset: createChronologyEntryReset,
    },
  ] = useLazyCreateChronologyEntryQuery();

  const [triggerUpdateTimelineEntryTime] = useLazyUpdateTimelineEntryTimeQuery();
  const [triggerUpdateChronologyEntryTime] = useLazyUpdateChronologyEntryTimeQuery();
  const [triggerUpdateTimelineEntryDate] = useLazyUpdateTimelineEntryDateQuery();
  const [triggerUpdateTimelineEntryProvider] = useLazyUpdateTimelineEntryProviderQuery();
  const [triggerUpdateTimelineEntrySummary] = useLazyUpdateTimelineEntrySummaryQuery();
  const [triggerUpdateTimelineEntryPages] = useLazyUpdateTimelineEntryPagesQuery();
  const [triggerUpdateTimelineEntryComment] = useLazyUpdateTimelineEntryCommentQuery();
  const [triggerUpdateChronologyEntryDate] = useLazyUpdateChronologyEntryDateQuery();
  const [triggerUpdateChronologyEntryProvider] = useLazyUpdateChronologyEntryProviderQuery();
  const [triggerUpdateChronologyEntrySummary] = useLazyUpdateChronologyEntrySummaryQuery();
  const [triggerUpdateChronologyEntryPages] = useLazyUpdateChronologyEntryPagesQuery();
  const [triggerUpdateChronologyEntryComment] = useLazyUpdateChronologyEntryCommentQuery();

  const dates: string[] = useMemo(() => {
    return listEntries
      .filter(
        (entry): entry is TimelineChronologyEntry =>
          typeof entry === 'object' && entry !== null && 'date' in entry && entry.date !== null,
      )
      .map((entry) => entry.date as string);
  }, [listEntries]);
  const times: string[] = useMemo(() => {
    return listEntries
      .filter(
        (entry): entry is TimelineChronologyEntry =>
          typeof entry === 'object' && entry !== null && 'time' in entry && entry.time !== null,
      )
      .map((entry) => entry.time as string);
  }, [listEntries]);

  // Helper function to sort entries within each date group
  const sortEntriesWithinDateGroups = (mixedEntries: (string | TimelineChronologyEntry | null | undefined)[]) => {
    const result: (string | TimelineChronologyEntry)[] = [];
    let currentGroup: TimelineChronologyEntry[] = [];

    for (const item of mixedEntries) {
      if (typeof item === 'string') {
        // This is a date header - process the current group and start a new one
        if (currentGroup.length > 0) {
          // Sort the current group by date/time
          // const sortedGroup = sortTimelineChronologyByDateAscending(currentGroup);
          //Removed Sorting for now
          const sortedGroup = currentGroup;
          result.push(...sortedGroup);
          currentGroup = [];
        }
        // Add the date header to result
        result.push(item);
      } else if (typeof item === 'object' && item !== null && 'id' in item) {
        // This is an entry - add to current group
        currentGroup.push(item);
      }
      // Skip null/undefined items
    }

    // Don't forget to process the last group
    if (currentGroup.length > 0) {
      // const sortedGroup = sortTimelineChronologyByDateAscending(currentGroup);
      //Removed Sorting for now
      const sortedGroup = currentGroup;
      result.push(...sortedGroup);
    }

    return result;
  };
  useEffect(() => {
    if (data?.entries && Array.isArray(data?.entries)) {
      // Process the mixed array to sort entries within each date group
      const processedEntries = sortEntriesWithinDateGroups(data.entries);

      setListEntries(processedEntries);

      // For currentListEntries, extract only the entry objects (maintain the sorted order)
      const entryObjects = processedEntries.filter(
        (item): item is TimelineChronologyEntry => typeof item === 'object' && item !== null && 'id' in item,
      );
      setCurrentListEntries(entryObjects);
    }
  }, [data?.entries]);

  useEffect(() => {
    if (caseId) {
      triggerGetCaseProviders({ caseId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  /** Scroll to newly created entry */
  useEffect(() => {
    if (!activeTimelineChronologyType) {
      return;
    }

    let entryId: string | undefined;

    if (activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      entryId = createTimelineEntryData?.entryId;
    }

    if (activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      entryId = createChronologyEntryData?.entryId;
    }

    if (!entryId) {
      return;
    }

    const indexToScroll = listEntries.findIndex(
      (entry) => typeof entry === 'object' && entry !== null && 'id' in entry && entry.id === entryId,
    );

    if (indexToScroll === -1) {
      return;
    }

    listRef.current?.scrollToIndex(getIndexThatIncludesInsertZones(indexToScroll - 1));

    if (activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      createTimelineEntryReset();
    }

    if (activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      createChronologyEntryReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimelineChronologyType, createChronologyEntryData?.entryId, createTimelineEntryData?.entryId, listEntries]);
  /** Scroll to edited entry */
  useEffect(() => {
    if (!editEntryId || !listEntries.length) {
      return;
    }

    const indexToScroll = listEntries.findIndex(
      (entry) => typeof entry === 'object' && entry !== null && 'id' in entry && entry.id === editEntryId,
    );

    if (indexToScroll === -1) {
      setEditEntryId(null);
      return;
    }

    listRef.current?.scrollToIndex(getIndexThatIncludesInsertZones(indexToScroll - 1));

    setEditEntryId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listEntries]);

  const handleTimelineClick = useCallback(
    (date: string) => {
      /** Parse input string (e.g., '2003-Nov') into a Date object */
      const [year, month] = date.split('-');
      const parsedDate = parse(`${month} ${year}`, 'MMM yyyy', new Date());

      const indexToScroll = dates.findIndex((timelineDate) => {
        const parsedTimelineDate = parse(timelineDate, 'yyyy-MM-dd', new Date());
        return isSameYear(parsedTimelineDate, parsedDate) && isSameMonth(parsedTimelineDate, parsedDate);
      });

      if (indexToScroll !== -1) {
        listRef.current?.scrollToIndex(getIndexThatIncludesInsertZones(indexToScroll));
      }
    },
    [dates],
  );

  /** called when user scrolls the list */
  const handleSetActiveEntryIndex = useCallback(
    (index: number | null) => {
      if (index !== null && activeTimelineChronologyType && dates.length) {
        dispatch(
          setTimelineChronologyActiveEntryIndex({
            key: activeTimelineChronologyType,
            data: index,
          }),
        );
        timelineRef.current?.scrollToRowByDate(dates[index]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTimelineChronologyType, dates],
  );

  const handleChangeSelectedEntryIds = useCallback(
    (id: string, checked?: boolean) => {
      if (!activeTimelineChronologyType) {
        return;
      }

      let newSelectedEntryIds = [...selectedEntryIds];

      if (checked) {
        newSelectedEntryIds.push(id);
      } else {
        newSelectedEntryIds = selectedEntryIds.filter((entryId) => entryId !== id);
      }

      dispatch(
        setTimelineChronologySelectedEntryIds({
          key: activeTimelineChronologyType,
          data: newSelectedEntryIds,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTimelineChronologyType, selectedEntryIds],
  );

  const handleSelectAllEntryIds = useCallback(() => {
    const allEntriesIds = currentListEntries.map((entry) => entry.id);
    if (activeTimelineChronologyType) {
      dispatch(
        setTimelineChronologySelectedEntryIds({
          key: activeTimelineChronologyType,
          data: [...allEntriesIds],
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimelineChronologyType, currentListEntries]);

  const handleDeselectAllEntryIds = useCallback(() => {
    if (activeTimelineChronologyType) {
      dispatch(
        setTimelineChronologySelectedEntryIds({
          key: activeTimelineChronologyType,
          data: [],
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimelineChronologyType]);

  const handleAddNewEntry = useCallback(
    (index: number) => {
      const indexThatIncludesInsertZones = getIndexThatIncludesInsertZones(index);
      const insertZoneBeforeEntry = indexThatIncludesInsertZones > 0 ? indexThatIncludesInsertZones - 1 : 0;

      listRef.current?.scrollToIndex(insertZoneBeforeEntry);

      const newEntry = {
        id: newEntryId,
        date: null,
        providerId: null,
        providerName: null,
        summary: null,
        comment: null,
        time: null,
        pages: [],
      } satisfies TimelineChronologyEntry;

      const updatedListEntries = [...currentListEntries];
      updatedListEntries.splice(index, 0, newEntry);

      setNewEntryIndex(indexThatIncludesInsertZones);
      setCurrentListEntries(updatedListEntries);
    },
    [currentListEntries, newEntryId],
  );

  const handleChangeEntryData = useCallback(
    (key: EntryDataKey, value: EntryDataValue) => {
      setEntryData((prevState) => ({ ...prevState, [key]: value }));

      /** Pages field needs validation */
      if (key !== 'pages' || typeof value !== 'string') {
        return;
      }

      if (!validatePagesInput(value)) {
        setIsPagesInputError(true);
        return;
      }

      setIsPagesInputError(false);

      if (
        activeBundlePageType === ActiveBundlePageType.LIGHT &&
        typeof data?.lightBundlePages === 'number' &&
        parseNumbers(value).some((pageNumber) => pageNumber > data.lightBundlePages!)
      ) {
        toastRef.current?.show({
          severity: 'warn',
          detail: 'The page number is greater than the number of pages in the Light Bundle',
          life: 5000,
        });
      }

      if (
        activeBundlePageType === ActiveBundlePageType.FULL &&
        typeof data?.fullBundlePages === 'number' &&
        parseNumbers(value).some((pageNumber) => pageNumber > data.fullBundlePages!)
      ) {
        toastRef.current?.show({
          severity: 'warn',
          detail: 'The page number is greater than the number of pages in the Full Bundle',
          life: 5000,
        });
      }
    },
    [activeBundlePageType, data?.fullBundlePages, data?.lightBundlePages],
  );

  const isValidEntryData: boolean = useMemo(() => {
    return !!caseId && !isPagesInputError && !!entryData.date;
  }, [caseId, entryData.date, isPagesInputError]);

  const handleCreateNewEntry = useCallback(() => {
    if (!isValidEntryData) {
      return;
    }

    if (activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      triggerCreateTimelineEntry({
        caseId: caseId!,
        bundlePageType: activeBundlePageType,
        timelineEntry: {
          date: format(entryData.date!, 'yyyy-MM-dd'),
          time: entryData.time ? format(entryData.time!, 'HH:mm') : null,
          providerId: entryData.provider ? entryData.provider.code : null,
          summary: entryData.summary,
          comment: entryData.comment,
          bundlePageNumbers: entryData.pages ? parseNumbers(entryData.pages) : [],
        },
      });
    }

    if (activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      triggerCreateChronologyEntry({
        caseId: caseId!,
        bundlePageType: activeBundlePageType,
        chronologyEntry: {
          date: format(entryData.date!, 'yyyy-MM-dd'),
          time: entryData.time ? format(entryData.time!, 'HH:mm') : null,
          providerId: entryData.provider ? entryData.provider.code : null,
          verbatimExtract: entryData.summary,
          comment: entryData.comment,
          bundlePageNumbers: entryData.pages ? parseNumbers(entryData.pages) : [],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBundlePageType, activeTimelineChronologyType, caseId, entryData, isValidEntryData]);

  const handleCancelCreateNewEntry = useCallback(() => {
    // Filter out only the entry objects for currentListEntries
    const entryObjects = listEntries.filter(
      (item): item is TimelineChronologyEntry => typeof item === 'object' && item !== null && 'id' in item,
    );
    setCurrentListEntries(entryObjects);
    setNewEntryIndex(null);
    setEntryData(initialEntryData);
    setIsPagesInputError(false);
  }, [listEntries]);

  useEffect(() => {
    if (isCreateTimelineEntryError) {
      toastRef.current?.show({
        severity: 'error',
        detail: 'An error occurred while creating the timeline entry',
        life: 5000,
      });
      return;
    }

    if (isCreateTimelineEntrySuccess) {
      const entryId = createTimelineEntryData?.entryId;
      // Update time if it was provided and is not the default "00:00:00"
      if (entryData.time && entryId) {
        const timeString = entryData.time ? format(entryData.time!, 'HH:mm') : null;

        triggerUpdateTimelineEntryTime({ entryId, time: timeString });
      }

      toastRef.current?.show({
        severity: 'success',
        detail: 'The timeline entry has been successfully created',
        life: 5000,
      });

      handleCancelCreateNewEntry();
      if (caseId) {
        dispatch(
          api.util.invalidateTags([
            { type: TIMELINE_TAG, id: caseId },
            { type: TIMELINE_UNDO_REDO_HISTORY_TAG, id: caseId },
          ]),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateTimelineEntryError, isCreateTimelineEntrySuccess]);

  useEffect(() => {
    if (isCreateChronologyEntryError) {
      // Refused by an endpoint lock - its toast has already been shown
      if (isEndpointLockError(createChronologyEntryError)) {
        return;
      }

      toastRef.current?.show({
        severity: 'error',
        detail: 'An error occurred while creating the chronology entry',
        life: 5000,
      });
      return;
    }

    if (isCreateChronologyEntrySuccess) {
      const entryId = createChronologyEntryData?.entryId;
      if (entryData.time && entryId) {
        const timeString = entryData.time ? format(entryData.time!, 'HH:mm') : null;
        triggerUpdateChronologyEntryTime({ entryId, time: timeString });
      }

      toastRef.current?.show({
        severity: 'success',
        detail: 'The chronology entry has been successfully created',
        life: 5000,
      });

      handleCancelCreateNewEntry();
      if (caseId) {
        dispatch(
          api.util.invalidateTags([
            { type: CHRONOLOGY_TAG, id: caseId },
            { type: CHRONOLOGY_UNDO_REDO_HISTORY_TAG, id: caseId },
          ]),
        );
      }
      // Tell any open Bundle (Full/Light) tabs a chronology entry was just
      // created here, so their thumbnail "in chronology" flags and the
      // chronology button popover refresh. Bundle listener clears
      // pageInfoCache and refetches the bundle; empty payload is fine
      // (it's only entry-ids that are needed on the delete path).
      sendMessageWithoutResponse(BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateChronologyEntryError, isCreateChronologyEntrySuccess]);

  const providersDropdownOptions: ProviderOption[] = useMemo(() => {
    return caseProvidersList.map((provider) => ({
      name: provider.providerName,
      code: provider.providerId,
    }));
  }, [caseProvidersList]);

  const handleEditEntryData = useCallback(
    (entry: TimelineChronologyEntry, index: number) => {
      listRef.current?.scrollToIndex(index > 0 ? index - 1 : 0);

      const pageNumbers = transformPagesToPageNumbers(entry.pages, activeBundlePageType);

      setEntryData((prevState) => ({
        ...prevState,
        ...(entry.date !== null && { date: new Date(entry.date) }),
        ...(entry.time ? { time: new Date(`1970-01-01T${entry.time}`) } : { time: null }),
        ...(entry.providerId &&
          entry.providerName && {
            provider: { name: entry.providerName, code: entry.providerId },
          }),
        ...(entry.summary && { summary: entry.summary }),
        ...(entry.comment && { comment: entry.comment }),
        ...(pageNumbers.length && { pages: pageNumbers.join(', ') }),
      }));
      setEditEntryIndex(index);
    },
    [activeBundlePageType],
  );

  const handleCancelEditEntry = () => {
    setEditEntryIndex(null);
    setEntryData(initialEntryData);
    setIsPagesInputError(false);
  };

  const handleSaveExistingTimelineEntry = useCallback(
    async (entry: TimelineChronologyEntry) => {
      const requestList = Object.entries(entryData).reduce((acc, [key, value]) => {
        if (areDatesDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateTimelineEntryDate({
              entryId: entry.id,
              date: format(value, 'yyyy-MM-dd'),
            }).unwrap(),
          ];
        }
        if (areTimesDifferent(key, value, entry)) {
          const timeString = value instanceof Date ? format(value, 'HH:mm') : value;
          const timeToSend = timeString ? timeString : null;
          return [
            ...acc,
            triggerUpdateTimelineEntryTime({
              entryId: entry.id,
              time: timeToSend,
            }).unwrap(),
          ];
        }
        if (areProvidersDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateTimelineEntryProvider({
              entryId: entry.id,
              providerId: value ? value.code : null,
            }).unwrap(),
          ];
        }

        if (areSummariesDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateTimelineEntrySummary({
              entryId: entry.id,
              summary: value,
            }).unwrap(),
          ];
        }

        if (arePagesDifferent(key, value, entry, activeBundlePageType)) {
          return [
            ...acc,
            triggerUpdateTimelineEntryPages({
              entryId: entry.id,
              bundlePageType: activeBundlePageType,
              newPageNumbers: parseNumbers(value),
            }).unwrap(),
          ];
        }

        if (areCommentsDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateTimelineEntryComment({
              entryId: entry.id,
              comment: value,
            }).unwrap(),
          ];
        }

        return acc;
      }, [] as RequestListAccumulator);

      /** no changes */
      if (!requestList.length) {
        handleCancelEditEntry();
        return;
      }

      try {
        await Promise.all(requestList);

        setEditEntryId(entry.id);

        toastRef.current?.show({
          severity: 'success',
          detail: 'All fields in the timeline entry have been successfully updated',
          life: 5000,
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        toastRef.current?.show({
          severity: 'error',
          detail: 'Timeline entry could not be created as the pages entered are not in light bundle',
          life: 5000,
        });
      } finally {
        handleCancelEditEntry();
        if (caseId) {
          dispatch(
            api.util.invalidateTags([
              { type: TIMELINE_TAG, id: caseId },
              { type: TIMELINE_UNDO_REDO_HISTORY_TAG, id: caseId },
            ]),
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeBundlePageType, entryData],
  );

  const handleSaveExistingChronologyEntry = useCallback(
    async (entry: TimelineChronologyEntry) => {
      const requestList = Object.entries(entryData).reduce((acc, [key, value]) => {
        if (areDatesDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateChronologyEntryDate({
              entryId: entry.id,
              date: format(value, 'yyyy-MM-dd'),
            }).unwrap(),
          ];
        }
        if (areTimesDifferent(key, value, entry)) {
          const timeString = value instanceof Date ? format(value, 'HH:mm') : value;
          const timeToSend = timeString ? timeString : null;
          return [
            ...acc,
            triggerUpdateChronologyEntryTime({
              entryId: entry.id,
              time: timeToSend,
            }).unwrap(),
          ];
        }
        if (areProvidersDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateChronologyEntryProvider({
              entryId: entry.id,
              providerId: value ? value.code : null,
            }).unwrap(),
          ];
        }

        if (areSummariesDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateChronologyEntrySummary({
              entryId: entry.id,
              summary: value,
            }).unwrap(),
          ];
        }

        if (arePagesDifferent(key, value, entry, activeBundlePageType)) {
          return [
            ...acc,
            triggerUpdateChronologyEntryPages({
              entryId: entry.id,
              bundlePageType: activeBundlePageType,
              newPageNumbers: parseNumbers(value),
            }).unwrap(),
          ];
        }

        if (areCommentsDifferent(key, value, entry)) {
          return [
            ...acc,
            triggerUpdateChronologyEntryComment({
              entryId: entry.id,
              comment: value,
            }).unwrap(),
          ];
        }

        return acc;
      }, [] as RequestListAccumulator);

      /** no changes */
      if (!requestList.length) {
        handleCancelEditEntry();
        return;
      }

      // If refused by an endpoint lock, its toast has already been shown and the form stays open to keep the draft
      let isRefusedByLock = false;

      try {
        await Promise.all(requestList);

        setEditEntryId(entry.id);

        toastRef.current?.show({
          severity: 'success',
          detail: 'All fields in the chronology entry have been successfully updated',
          life: 5000,
        });
      } catch (error) {
        isRefusedByLock = isEndpointLockError(error);

        if (!isRefusedByLock) {
          toastRef.current?.show({
            severity: 'error',
            detail: 'Chronology entry could not be created as the pages entered are not in light bundle',
            life: 5000,
          });
        }
      } finally {
        if (!isRefusedByLock) {
          handleCancelEditEntry();
        }
        if (caseId) {
          dispatch(
            api.util.invalidateTags([
              { type: CHRONOLOGY_TAG, id: caseId },
              { type: CHRONOLOGY_UNDO_REDO_HISTORY_TAG, id: caseId },
            ]),
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeBundlePageType, entryData],
  );

  // const handleSaveExistingEntry = useCallback(async () => {
  //   if (!isValidEntryData || !editEntryIndex || !activeTimelineChronologyType) {
  //     return;
  //   }

  //   const activeEntry =
  //     listEntries[
  //     /** Skip insert zones */
  //     (editEntryIndex - 1) / 2
  //     ];

  //   // Type guard to ensure activeEntry is a valid TimelineChronologyEntry
  //   if (typeof activeEntry === 'object' && activeEntry !== null && 'id' in activeEntry) {
  //     if (activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
  //       handleSaveExistingTimelineEntry(activeEntry);
  //     }

  //     if (activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
  //       handleSaveExistingChronologyEntry(activeEntry);
  //     }
  //   }
  // }, [
  //   activeTimelineChronologyType,
  //   editEntryIndex,
  //   handleSaveExistingChronologyEntry,
  //   handleSaveExistingTimelineEntry,
  //   isValidEntryData,
  //   listEntries
  // ]);
  const handleSaveExistingEntry = useCallback(async () => {
    if (!isValidEntryData || !editEntryIndex || !activeTimelineChronologyType) {
      return;
    }

    // Convert virtual index back to data index
    const dataIndex = Math.floor(editEntryIndex / 2);

    // Find the actual entry in listEntries
    let entryCount = 0;
    let activeEntry: TimelineChronologyEntry | null = null;

    for (const item of listEntries) {
      if (typeof item === 'object' && item !== null && 'id' in item) {
        if (entryCount === dataIndex) {
          activeEntry = item;
          break;
        }
        entryCount++;
      }
    }

    if (!activeEntry) {
      console.error('Could not find entry at index:', dataIndex);
      return;
    }

    if (activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      handleSaveExistingTimelineEntry(activeEntry);
    }

    if (activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      handleSaveExistingChronologyEntry(activeEntry);
    }
  }, [
    activeTimelineChronologyType,
    editEntryIndex,
    handleSaveExistingChronologyEntry,
    handleSaveExistingTimelineEntry,
    isValidEntryData,
    listEntries,
  ]);
  return {
    timelineRef,
    listRef,
    toastRef,
    activeBundlePageType,
    activeTimelineChronologyType,
    listEntries: currentListEntries, // For Footer component expecting TimelineChronologyEntry[]
    mixedListEntries: listEntries, // For List component to show dates and entries
    currentListEntries,
    dates,
    times,
    activeEntryIndex,
    selectedEntryIds,
    newEntryIndex,
    editEntryIndex,
    entryData,
    providersDropdownOptions,
    isPagesInputError,
    isValidEntryData,
    handleTimelineClick,
    handleSetActiveEntryIndex,
    handleChangeSelectedEntryIds,
    handleSelectAllEntryIds,
    handleDeselectAllEntryIds,
    handleAddNewEntry,
    handleChangeEntryData,
    handleCreateNewEntry,
    handleCancelCreateNewEntry,
    handleEditEntryData,
    handleCancelEditEntry,
    handleSaveExistingEntry,
  };
};
